import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    IconButton,
    Popover,
    TextField,
    Button,
    CircularProgress,
    InputAdornment,
    Tooltip
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import EmailIcon from "@mui/icons-material/Email";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import { Card } from "@mui/material";
import MDTypography from "components/MDTypography";
import ReportGenService from "services/reportgen-service";
import MDButton from "components/MDButton";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW]

function ShareChartButton({
    chartIds,
    selectedProfiles,
    dateRange,
    isPredictionEnabled,
    contentChartRef,
    isLocked,
    onLockedClick,
    onRBACClick,
    hasPermission
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [emails, setEmails] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [showDemoPopup, setShowDemoPopup] = useState(false); // [NEW] Demo Popup State

    // Pre-populate with current user email if available (mocked for now or passed via props/context if needed)
    // const [emails, setEmails] = useState(currentUser?.email || ""); 

    const handleClick = (event) => {
        if (!hasPermission) {
            if (onRBACClick) onRBACClick();
            return;
        }

        if (isLocked) {
            if (onLockedClick) onLockedClick();
            return;
        }
        setAnchorEl(event.currentTarget);
        setSuccess(false);
        setError("");
    };

    const handleClose = () => {
        setAnchorEl(null);
        setIsLoading(false);
    };

    const open = Boolean(anchorEl);
    const id = open ? "share-popover" : undefined;

    const validateEmails = (emailString) => {
        const emailList = emailString.split(",").map(e => e.trim()).filter(e => e);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (emailList.length === 0) return false;

        for (const email of emailList) {
            if (!emailRegex.test(email)) {
                return false;
            }
        }
        return true;
    };

    const handleSend = async () => {
        // [NEW] Live Demo Restriction
        const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
        if (isDemo) {
            setShowDemoPopup(true);
            return;
        }

        if (!validateEmails(emails)) {
            setError("Please enter valid email address(es).");
            return;
        }

        try {
            setIsLoading(true);
            setError("");

            if (!selectedProfiles || selectedProfiles.length === 0) {
                setError("No profiles selected.");
                return;
            }

            // Reuse data preparation logic
            const CONTENT_CHART_ID = "content-performance-over-time-chart";
            const finalChartConfigs = {};
            const isContentChartActive = chartIds.includes(CONTENT_CHART_ID);

            if (isContentChartActive && contentChartRef && contentChartRef.current) {
                const chartInstance = contentChartRef.current;
                if (typeof chartInstance.getCurrentConfig === 'function') {
                    const config = chartInstance.getCurrentConfig();
                    if (config && config.entityIdOrder?.length > 0) {
                        finalChartConfigs[CONTENT_CHART_ID] = config;
                    }
                }
            }

            const payloadProfileData = selectedProfiles.map(profile => ({
                profileId: profile.id,
                platform: profile.platform,
                name: profile.name,
                profilePictureUrl: profile.profilePictureUrl,
                fromDate: dateRange[0].toISOString(),
                toDate: dateRange[1].toISOString(),
            }));

            const recipientList = emails.split(",").map(e => e.trim()).filter(e => e);

            await ReportGenService.emailReport(
                payloadProfileData,
                isPredictionEnabled,
                chartIds,
                finalChartConfigs,
                recipientList
            );

            setSuccess(true);
            setTimeout(() => {
                handleClose();
                setSuccess(false);
                setEmails("");
            }, 2000);

        } catch (err) {
            console.error("Failed to share report:", err);
            setError("Failed to send email. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <MDBox>
            <Tooltip title="Share Report" arrow placement="bottom">
                <Card
                    onClick={handleClick}
                    sx={{
                        cursor: "pointer",
                        width: 48, // Adjusted to match generic button size (48px)
                        height: 48,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "12px",
                        backgroundColor: "#ffffff", // Use generic white bg like ToolSelector buttons
                        boxShadow: 1,
                        border: "1px solid #ddd",
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                            transform: "translateY(-2px)", // Generic hover effect
                            boxShadow: 3,
                            backgroundColor: "#f8f9fa",
                        },
                        "&:active": {
                            backgroundColor: "#e9ecef",
                            boxShadow: 1,
                        },
                    }}
                >
                    <IconButton sx={{ padding: 0 }}>
                        <ShareIcon sx={{ color: "#000000" }} />
                    </IconButton>
                </Card>
            </Tooltip>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
                disableScrollLock={true}
                PaperProps={{
                    sx: {
                        width: 400,
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: "#ffffff !important",
                        boxShadow: 3
                    }
                }}
            >
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDTypography variant="h6">Share Report</MDTypography>
                    <IconButton size="small" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </MDBox>

                <MDTypography variant="caption" color="text" sx={{ mb: 2, display: "block" }}>
                    Enter email addresses separated by commas.
                </MDTypography>

                {success ? (
                    <MDBox
                        sx={{
                            p: 2,
                            bgcolor: "success.light",
                            borderRadius: 2,
                            color: "white",
                            textAlign: "center"
                        }}
                    >
                        <MDTypography variant="button" color="white" fontWeight="bold">
                            Email Sent Successfully!
                        </MDTypography>
                    </MDBox>
                ) : (
                    <>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="user@example.com, boss@example.com"
                            value={emails}
                            onChange={(e) => setEmails(e.target.value)}
                            error={!!error}
                            helperText={error}
                            multiline
                            rows={1}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ pl: 1.5 }}>
                                        <EmailIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <MDBox display="flex" justifyContent="flex-end">
                            <MDButton
                                variant="contained"
                                color="accent"
                                onClick={handleSend}
                                disabled={isLoading}
                                startIcon={isLoading && <CircularProgress size={16} color="white" />}
                            >
                                {isLoading ? "Sending..." : "Go"}
                            </MDButton>
                        </MDBox>
                    </>
                )}
            </Popover>

            {/* [NEW] Live Demo Restriction Popup */}
            <LiveDemoRestriction
                open={showDemoPopup}
                onClose={() => setShowDemoPopup(false)}
                featureName="Sharing Report"
            />
        </MDBox>
    );
}

ShareChartButton.propTypes = {
    chartIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedProfiles: PropTypes.array.isRequired,
    dateRange: PropTypes.array.isRequired,
    isPredictionEnabled: PropTypes.bool.isRequired,
    contentChartRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.object }),
    ]).isRequired,

    isLocked: PropTypes.bool,
    onLockedClick: PropTypes.func,
    onRBACClick: PropTypes.func,
    hasPermission: PropTypes.bool,
};

export default ShareChartButton;
